import dayjs from "dayjs";
import Image from "next/image";
import React, { useState } from "react";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import { getIdToken, requireIdToken } from "../utils/firebaseAuth";
import Link from "next/link";
import {
  BsThreeDots,
  BsThreeDotsVertical,
  BsPinAngle,
  BsPinAngleFill,
} from "react-icons/bs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@jammable/ui-components/DropdownMenu";
import { FaTrash, FaHeart, FaRegHeart } from "react-icons/fa";
import dynamic from "next/dynamic";
import { useModal } from "@/context/ModalContext";
import { AiFillWarning } from "react-icons/ai";
import useAuthModal from "@/hooks/useAuthModal";
import { Input } from "@jammable/ui-components/Forms/Input";
import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import { useQueryClient } from "react-query";
import { Spinner } from "@jammable/ui-components/Loader";
import Dropdown from "./dropdown/Dropdown.js";
import { useRouter } from "next/router";
import { useT } from "@jammable/ui-core/src/i18n";

// Opened from the comment's menu only; loaded then (they bring Formik/yup).
const modalLoading = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
    <Spinner size={8} className="tw-border-3" />
  </div>
);
const DeleteCommentModal = dynamic(
  () => import("../components/modals/DeleteCommentModal"),
  { ssr: false, loading: modalLoading },
);
const ReportModal = dynamic(() => import("./modals/ReportModal"), {
  ssr: false,
  loading: modalLoading,
});

const ModelCommentItem = ({ comment, model, page, className }) => {
  dayjs.extend(relativeTime);
  const t = useT();
  const [likes, setLikes] = useState(comment?.likes ?? 0);
  const [isLiked, setIsLiked] = useState(comment?.isLiked ?? false);
  const { checkAuthAndExecute } = useAuthModal();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [visibleReplies, setVisibleReplies] = useState(2);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replyPagination, setReplyPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalReplies: 0,
  });

  const formatRelativeTime = (date) => {
    const duration = dayjs().diff(dayjs(date), "minute");
    if (duration < 1) return t("voice.comments.justNow");
    if (duration < 60) return t("voice.comments.minutes", { n: duration });
    const hours = Math.floor(duration / 60);
    if (hours < 24) return t("voice.comments.hours", { n: hours });
    const days = Math.floor(hours / 24);
    return t("voice.comments.days", { n: days });
  };
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const fetchReplies = async (page = 1) => {
    setIsLoadingReplies(true);

    try {
      const token = await getIdToken();
      const res = await axios.get("/api/comment/getCommentReplies", {
        params: {
          commentId: comment._id,
          page,
          limit: 5,
        },
        headers: {
          Authorization: token,
        },
      });

      setReplies((prev) =>
        page === 1 ? res.data.replies : [...prev, ...res.data.replies],
      );
      setReplyPagination(res.data.pagination);
    } catch (error) {
      console.error("Error fetching replies:", error);
      toast.error(t("voice.comments.repliesFailed"));
    } finally {
      setIsLoadingReplies(false);
    }
  };

  const loadMoreReplies = () => {
    if (replyPagination.currentPage < replyPagination.totalPages) {
      fetchReplies(replyPagination.currentPage + 1);
    }
  };

  // One field, so no Formik/yup: same rules as their schema (required, at
  // most 500 characters); an invalid reply just isn't sent.
  const [reply, setReply] = useState("");
  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply || reply.length > 500) return;
    if (!checkAuthAndExecute()) return false;
    if (reply.trim().length === 0) return;
    if (!checkAuthAndExecute()) return false;
    setCommenting(true);
    const token = await requireIdToken();
    axios
      .post(
        `/api/comment/createComment`,
        {
          body: reply,
          modelId: model?._id,
          parentid: comment._id,
        },
        {
          headers: { Authorization: token },
        },
      )
      .then(() => {
        queryClient.invalidateQueries(["comments", model?._id]).then(() => {
          toast.success(t("voice.comments.posted"));
          setCommenting(false);
          setIsReplyOpen(false);
          setReply("");
        });
      })
      .catch((err) => {
        setCommenting(false);
        toast.error(err?.response?.data?.message);
      });
  };
  const router = useRouter();

  return (
    <div
      onClick={() => {}}
      key={comment?._id}
      className={`tw-flex tw-flex-col tw-gap-2 tw-justify-between tw-items-center ${
        comment.parentid && "tw-p-1"
      } ${className}`}
    >
      <div className="tw-flex tw-justify-between tw-w-full">
        <div className="tw-flex tw-gap-3">
          <Image
            alt={t("voice.comments.avatarAlt")}
            className="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-bg-jam-line tw-object-cover"
            src={comment?.user?.avatar}
            width={40}
            height={40}
            onClick={() => {
              router.push(`/@${comment?.user?.username}`);
            }}
          />
          <div className="tw-flex tw-flex-col tw-text-[15px] tw-font-semibold tw-text-jam-ink">
            <div className=" tw-flex ">
              <Link
                href={`/@${comment?.user?.username}`}
                className="tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline"
              >
                {comment?.user?.username}
              </Link>

              {comment?.isCreator && (
                <div className="tw-ml-2 tw-flex tw-items-center tw-rounded-full tw-bg-jam-lime tw-px-2 tw-text-xs tw-font-extrabold tw-text-jam-ink">
                  {t("voice.comments.creator")}
                </div>
              )}
              <span className="tw-ml-2 tw-text-sm tw-font-semibold tw-text-jam-muted">
                · {formatRelativeTime(comment?.createdAt)}
              </span>
            </div>
            <div className="tw-mt-1"> {comment?.body}</div>

            <div className="tw-mt-1.5 tw-flex tw-w-full tw-items-center tw-gap-3.5 tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
              {comment.pinned && (
                <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs">
                  <BsPinAngleFill size={14} />
                  {t("voice.comments.pinned")}
                </div>
              )}
              <div
                onClick={async () => {
                  if (!checkAuthAndExecute()) return false;
                  const token = await requireIdToken();

                  if (isLiked) {
                    setIsLiked(false);
                    setLikes(likes - 1);
                  } else {
                    setIsLiked(true);
                    setLikes(likes + 1);
                  }

                  axios.post(
                    `/api/comment/likeComment`,
                    { commentId: comment?._id },
                    {
                      headers: {
                        Authorization: token,
                      },
                    },
                  );
                }}
                className="tw-flex tw-cursor-pointer tw tw-text-md tw-items-center tw-gap-1 tw-font-bold"
              >
                {isLiked ? (
                  <FaHeart size={15} color="#782dc8" />
                ) : (
                  <FaRegHeart size={15} color="#6B5690" />
                )}
                <span className="tw-text-[13px] tw-normal-nums">{likes}</span>
              </div>
              {comment.depth >= 0 || comment.loading ? null : (
                <div
                  onClick={() => {
                    setIsReplyOpen(!isReplyOpen);
                  }}
                  className="tw-flex tw-cursor-pointer tw-items-center hover:tw-text-jam-purple"
                >
                  {t("voice.comments.reply")}
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <Dropdown
            dropdownItems={
              <>
                {model?.isOwner && (
                  <DropdownMenuItem
                    onClick={async () => {
                      const token = await requireIdToken();

                      try {
                        const res = await axios.post(
                          `/api/comment/pinComment`,
                          { commentId: comment?._id },
                          {
                            headers: { Authorization: token },
                          },
                        );

                        queryClient.invalidateQueries(["comments", model?._id]);
                        toast.success(
                          res.data.pinned
                            ? t("voice.comments.pinnedToast")
                            : t("voice.comments.unpinnedToast"),
                        );
                      } catch (err) {
                        toast.error(t("voice.comments.pinFailed"));
                      }
                    }}
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-p-2 tw-pl-3 tw-font-extrabold tw-text-jam-ink hover:tw-bg-jam-lav"
                  >
                    <BsPinAngleFill size={16} />
                    {comment.pinned
                      ? t("voice.comments.unpin")
                      : t("voice.comments.pin")}
                  </DropdownMenuItem>
                )}
                {comment?.isMine ? (
                  <DropdownMenuItem
                    onClick={() => {
                      openModal(
                        "delete-comment-modal",
                        <DeleteCommentModal
                          comment={comment}
                          // sortBy={sortBy}
                          closeModal={() => closeModal("delete-comment-modal")}
                        />,
                      );
                    }}
                    className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                  >
                    <FaTrash size={16} />
                    {t("voice.comments.delete")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!checkAuthAndExecute()) return false;
                      openModal(
                        "report-modal",
                        <ReportModal
                          comment={comment}
                          closeModal={() => closeModal("delete-comment-modal")}
                          item={comment}
                          type="comment"
                        />,
                      );
                    }}
                    className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                  >
                    <AiFillWarning size={18} />
                    {t("voice.comments.report")}
                  </DropdownMenuItem>
                )}
              </>
            }
          >
            <div className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-p-2 tw-text-jam-muted tw-transition-colors hover:tw-bg-jam-lav">
              <BsThreeDots size={15} />
            </div>
          </Dropdown>
        </div>
      </div>
      {isReplyOpen && (
        <form
          onSubmit={submitReply}
          className="tw-w-full tw-flex tw-gap-2 tw-items-end"
        >
          <Input
            aria-label={t("voice.comments.replyLabel")}
            placeholder={t("voice.comments.replyPlaceholder")}
            rightSectionWidth="80"
            className="tw-mt-0 tw-h-10 tw-w-full tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-sm tw-font-bold tw-text-jam-ink"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            name="reply"
            disabled={commenting}
          />
          <Button
            className="tw-h-10 tw-w-20 tw-rounded-full tw-bg-jam-purple tw-text-xs tw-font-extrabold"
            type="submit"
          >
            {commenting ? (
              <Spinner className="tw-border-white" size={4} />
            ) : (
              t("voice.comments.reply")
            )}
          </Button>
        </form>
      )}
      {replies.map((reply) => (
        <ModelCommentItem
          key={reply._id}
          comment={reply}
          page={page}
          model={model}
          className="tw-w-full tw-pl-14"
        />
      ))}

      {replyPagination.totalPages > replyPagination.currentPage &&
        !comment.parentid &&
        comment.replyCount > 0 && (
          <div className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-pl-[52px]">
            {/* <div className="tw-w-full tw-h-0.5 tw-bg-gray-200 tw-rounded-full" /> */}

            <SecondaryButton
              style={{
                fontSize: 14,
                padding: 14,
                paddingTop: 8,
                paddingBottom: 8,
                height: 40,
                display: "flex",
              }}
              onClick={() => {
                if (replies.length === 0) {
                  fetchReplies(1);
                } else {
                  loadMoreReplies();
                }
              }}
              className="tw-mb-2 tw-mt-1 tw-w-max tw-rounded-full tw-border-none tw-bg-jam-lav tw-font-extrabold tw-text-jam-purple"
              variant="secondary"
            >
              {t(
                replyPagination.currentPage === 0
                  ? comment.replyCount === 1
                    ? "voice.comments.viewReply.one"
                    : "voice.comments.viewReply.other"
                  : "voice.comments.viewMore",
                { n: comment.replyCount - replyPagination.currentPage * 5 },
              )}
            </SecondaryButton>
            {/* <div className="tw-w-full tw-h-0.5 tw-bg-gray-200 tw-rounded-full" /> */}
          </div>
        )}
    </div>
  );
};

export default ModelCommentItem;
